import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaDate,
  apaName,
  type CitationParts,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  parsePersonName,
  tidyParts,
  yearOf,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  fullDate,
  get,
  italic,
  mainTitle,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaInText,
  nlmDate,
  nlmName,
  type SpecialValues,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A clinical trial registration — a record in ClinicalTrials.gov, the ISRCTN registry,
 * the EU registers or ANZCTR — in APA 7, AMA 11 and Vancouver (NLM).
 *
 * Sources checked (October 2026):
 * - Vancouver: NLM, *Citing Medicine*, 2nd ed., chapter "Databases/Retrieval
 *   Systems/Datasets on the Internet" (NBK7273, updated 2018), "Contributions to
 *   Databases", whose own ClinicalTrials.gov example is: "Cannon R. Rilonacept to
 *   improve artery function in patients with atherosclerosis. 2006 Dec 29 [last updated
 *   2012 Apr 25; …; cited 2015 Apr 25]. In: ClinicalTrials.gov [Internet]. Bethesda
 *   (MD): U.S. National Library of Medicine. 2000 -   . Available from:
 *   http://clinicaltrials.gov/show/NCT00417417 ClinicalTrials.gov Identifier:
 *   NCT00417417." Author = the record's responsible party; dates as "2006 Dec 29";
 *   update and citation dates in one bracket; the identifier after the URL, introduced
 *   by the database's own wording. The open range "2000 -   ." is three no-break
 *   spaces, as NLM prints it. The WHO ICTRP note "How to cite a record on a clinical
 *   trials register" applies the same NLM rules to ANZCTR, ISRCTN and other registers.
 * - AMA 11: AMA Style Insider, "Questions From Users of the Manual" (March 25, 2019):
 *   a trial is cited "most like a website", and the identifier is included —
 *   "Evaluation of phage therapy … (PHAGOBURN). ClinicalTrials.gov identifier:
 *   NCT02116010. … Updated July 23, 2015. Accessed …". AMA 11 (§3.15) moved the URL
 *   to the end of the reference, which is the form the Cedarville University and UW–
 *   Madison AMA 11 guides print. No author element, as in AMA's example.
 * - APA 7 has no clinical-trial example. The record is a webpage (§10.16: author, the
 *   date "last updated" when it belongs to the content cited, italic title, site name,
 *   URL), and the registry number goes in parentheses after the title, as APA does for
 *   ERIC document numbers (§10.8) and report numbers (§10.4).
 * - Registry facts: ClinicalTrials.gov (NLM, Bethesda, 2000) per Citing Medicine; the
 *   ISRCTN registry (BioMed Central, London, launched 2000, record DOIs
 *   10.1186/ISRCTN…) per isrctn.com and Crossref; ANZCTR (NHMRC Clinical Trials Centre,
 *   University of Sydney, 2005) per the WHO ICTRP; the EU Clinical Trials Register
 *   (EMA, launched 22 March 2011) and CTIS's public site, "Clinical Trials in the
 *   European Union" (EMA, live 31 January 2022), per EMA. EMA has been in Amsterdam
 *   since 2019.
 */

export const CLINICAL_TRIAL_FIELDS = {
  registry: "registry",
  registryName: "registryName",
  registryPublisher: "registryPublisher",
  identifier: "identifier",
  title: "title",
  authorType: "authorType",
  author: "author",
  firstPosted: "firstPosted",
  lastUpdated: "lastUpdated",
  accessed: "accessed",
  url: "url",
} as const;

const F = CLINICAL_TRIAL_FIELDS;

export type TrialRegistry =
  | "clinicaltrials"
  | "isrctn"
  | "euctr"
  | "ctis"
  | "anzctr"
  | "other";

export const TRIAL_REGISTRIES: readonly TrialRegistry[] = [
  "clinicaltrials",
  "isrctn",
  "euctr",
  "ctis",
  "anzctr",
  "other",
];

/** NLM's open-ended date range: a hyphen, three no-break spaces, a period. */
const OPEN_RANGE = " -   .";

interface RegistryFacts {
  /** Site name in APA, database name in NLM. */
  name: string;
  /** The words before the number: "ClinicalTrials.gov identifier". */
  amaLabel: string;
  nlmLabel: string;
  /** Put before the number in APA's parenthesis when the number alone is ambiguous. */
  apaPrefix: string;
  /** Citing Medicine's place, publisher and start date for the registry. */
  nlmPublication: string;
  /** The record's permanent address, when the registry has one. */
  recordUrl?: (id: string) => string;
}

const REGISTRIES: Readonly<
  Record<Exclude<TrialRegistry, "other">, RegistryFacts>
> = {
  clinicaltrials: {
    name: "ClinicalTrials.gov",
    amaLabel: "ClinicalTrials.gov identifier",
    nlmLabel: "ClinicalTrials.gov Identifier",
    apaPrefix: "",
    nlmPublication: `Bethesda (MD): U.S. National Library of Medicine. 2000${OPEN_RANGE}`,
    recordUrl: (id) => `https://clinicaltrials.gov/study/${id.toUpperCase()}`,
  },
  isrctn: {
    name: "ISRCTN registry",
    amaLabel: "ISRCTN identifier",
    nlmLabel: "ISRCTN Identifier",
    apaPrefix: "",
    nlmPublication: `London: BioMed Central. 2000${OPEN_RANGE}`,
    recordUrl: (id) => `https://doi.org/10.1186/${id.toUpperCase()}`,
  },
  euctr: {
    name: "EU Clinical Trials Register",
    amaLabel: "EudraCT number",
    nlmLabel: "EudraCT Number",
    apaPrefix: "EudraCT ",
    nlmPublication: `Amsterdam: European Medicines Agency. 2011${OPEN_RANGE}`,
  },
  ctis: {
    name: "Clinical Trials in the European Union",
    amaLabel: "EU CT number",
    nlmLabel: "EU CT Number",
    apaPrefix: "EU CT ",
    nlmPublication: `Amsterdam: European Medicines Agency. 2022${OPEN_RANGE}`,
  },
  anzctr: {
    name: "Australian New Zealand Clinical Trials Registry",
    amaLabel: "ANZCTR identifier",
    nlmLabel: "ANZCTR Identifier",
    apaPrefix: "",
    nlmPublication: `Sydney (NSW): NHMRC Clinical Trials Centre, University of Sydney (Australia). 2005${OPEN_RANGE}`,
  },
};

const RE_NCT = /^NCT\d{8}$/i;
const RE_ISRCTN = /^ISRCTN\d{8}$/i;
const RE_PREFIXED_ID = /^(?:NCT|ISRCTN|ACTRN)\d+$/i;
const RE_SPACES = /\s+/g;

/** "nct 04381936" → "NCT04381936" when it is a well-formed NCT number. */
export function normalizeNct(text: string): string | null {
  const compact = text.replace(RE_SPACES, "");
  return RE_NCT.test(compact) ? compact.toUpperCase() : null;
}

/** Registry numbers with a letter prefix are printed in capitals: "NCT04381936". */
function normalizeId(text: string): string {
  const compact = text.replace(RE_SPACES, "");
  return RE_PREFIXED_ID.test(compact) ? compact.toUpperCase() : text.trim();
}

function registryOf(values: SpecialValues): TrialRegistry {
  const chosen = get(values, F.registry) as TrialRegistry;
  return TRIAL_REGISTRIES.includes(chosen) ? chosen : "clinicaltrials";
}

/** The registry's facts, with "other" built from what the user typed. */
export function registryFacts(values: SpecialValues): RegistryFacts {
  const registry = registryOf(values);
  if (registry !== "other") {
    return REGISTRIES[registry];
  }
  const name = get(values, F.registryName) || "Trial registry";
  const publisher = get(values, F.registryPublisher);
  return {
    name,
    amaLabel: `${name} identifier`,
    nlmLabel: `${name} Identifier`,
    apaPrefix: "",
    nlmPublication: publisher
      ? `${publisher}${closer(publisher)} [date unknown]${OPEN_RANGE}`
      : "",
  };
}

/** The URL typed, or the record's permanent address on registries that have one. */
export function trialUrl(values: SpecialValues): string {
  const typed = get(values, F.url);
  if (typed) {
    return typed;
  }
  const id = normalizeId(get(values, F.identifier));
  const facts = registryFacts(values);
  if (!(id && facts.recordUrl)) {
    return "";
  }
  const registry = registryOf(values);
  const valid =
    (registry === "clinicaltrials" && RE_NCT.test(id)) ||
    (registry === "isrctn" && RE_ISRCTN.test(id));
  return valid ? facts.recordUrl(id) : "";
}

function authorOf(values: SpecialValues): PersonName | null {
  const raw = get(values, F.author);
  if (raw === "") {
    return null;
  }
  if (get(values, F.authorType) === "person") {
    return parsePersonName(raw);
  }
  return { given: [], family: raw, organization: true };
}

export interface ClinicalTrialLabels {
  apa: string;
  ama: string;
  vancouver: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  apaNote: string;
  amaNote: string;
  vancouverNote: string;
}

export function clinicalTrialIsReady(values: SpecialValues): boolean {
  const named =
    registryOf(values) !== "other" || get(values, F.registryName) !== "";
  return (
    named && get(values, F.title) !== "" && get(values, F.identifier) !== ""
  );
}

interface Trial {
  facts: RegistryFacts;
  id: string;
  title: string;
  author: PersonName | null;
  firstPosted: PartialDate | null;
  lastUpdated: PartialDate | null;
  accessed: PartialDate | null;
  url: string;
}

function readTrial(values: SpecialValues): Trial {
  return {
    facts: registryFacts(values),
    id: normalizeId(get(values, F.identifier)),
    title: get(values, F.title),
    author: authorOf(values),
    firstPosted: parsePartialDate(get(values, F.firstPosted)),
    lastUpdated: parsePartialDate(get(values, F.lastUpdated)),
    accessed: parsePartialDate(get(values, F.accessed)),
    url: trialUrl(values),
  };
}

/*
 * APA: Author. (Date). *Title* (Identifier). Registry. URL — the date is the last
 * update when there is one (§10.16), else the date first posted. With no author, the
 * title moves to the author position and the in-text citation uses it.
 */
function apaOutput(trial: Trial, labels: ClinicalTrialLabels): CitationOutput {
  const date = trial.lastUpdated ?? trial.firstPosted;
  const dated = `(${apaDate(date)}).`;
  const number = `(${trial.facts.apaPrefix}${trial.id})`;
  const source = trial.url
    ? `${trial.facts.name}. ${trial.url}`
    : `${trial.facts.name}.`;
  const year = yearOf(date);
  let reference: CitationParts;
  let parenthetical: CitationParts;
  let narrative: CitationParts;
  if (trial.author) {
    const author = apaName(trial.author);
    reference = [
      plain(`${author}${closer(author)} ${dated} `),
      italic(trial.title),
      plain(` ${number}. ${source}`),
    ];
    parenthetical = [plain(apaInText([trial.author], year, false))];
    narrative = [plain(apaInText([trial.author], year, true))];
  } else {
    const short = titleCase(mainTitle(trial.title));
    reference = [italic(trial.title), plain(` ${number}. ${dated} ${source}`)];
    parenthetical = [plain("("), italic(short), plain(`, ${year})`)];
    narrative = [italic(short), plain(` (${year})`)];
  }
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: tidyParts(reference) },
      { label: labels.parenthetical, parts: parenthetical },
      { label: labels.narrative, parts: narrative },
    ],
    note: labels.apaNote,
  };
}

/*
 * AMA: Title. Registry identifier: NCT…. Updated Month D, YYYY. Accessed Month D, YYYY.
 * URL — "Published" stands in for "Updated" when only the first-posted date is known.
 */
function amaReference(trial: Trial): string {
  const pieces = [
    `${trial.title}${closer(trial.title)}`,
    `${trial.facts.amaLabel}: ${trial.id}.`,
  ];
  if (trial.lastUpdated) {
    pieces.push(`Updated ${fullDate(trial.lastUpdated)}.`);
  } else if (trial.firstPosted) {
    pieces.push(`Published ${fullDate(trial.firstPosted)}.`);
  }
  if (trial.accessed) {
    pieces.push(`Accessed ${fullDate(trial.accessed)}.`);
  }
  if (trial.url) {
    pieces.push(trial.url);
  }
  return pieces.join(" ");
}

/*
 * NLM: Author. Title. 2020 May 11 [last updated …; cited …]. In: Registry [Internet].
 * Place: Publisher. Start -   . Available from: URL Registry Identifier: NCT….
 */
function nlmDates(trial: Trial): string {
  const bracket: string[] = [];
  if (trial.lastUpdated) {
    bracket.push(`last updated ${nlmDate(trial.lastUpdated)}`);
  }
  if (trial.accessed) {
    bracket.push(`cited ${nlmDate(trial.accessed)}`);
  }
  // Citing Medicine marks a missing publication date "[date unknown]".
  const posted = nlmDate(trial.firstPosted) || "[date unknown]";
  const inBrackets = bracket.length > 0 ? ` [${bracket.join("; ")}]` : "";
  return `${posted}${inBrackets}.`;
}

function nlmReference(trial: Trial): string {
  const pieces: string[] = [];
  if (trial.author) {
    pieces.push(`${nlmName(trial.author)}.`);
  }
  pieces.push(`${trial.title}${closer(trial.title)}`);
  pieces.push(nlmDates(trial));
  pieces.push(`In: ${trial.facts.name} [Internet].`);
  pieces.push(trial.facts.nlmPublication);
  if (trial.url) {
    pieces.push(`Available from: ${trial.url}`);
  }
  pieces.push(`${trial.facts.nlmLabel}: ${trial.id}.`);
  return pieces.filter(Boolean).join(" ");
}

export function formatClinicalTrial(
  values: SpecialValues,
  labels: ClinicalTrialLabels
): CitationOutput[] {
  const trial = readTrial(values);
  return [
    apaOutput(trial, labels),
    {
      key: "ama",
      label: labels.ama,
      entries: [
        { label: labels.referenceList, parts: [plain(amaReference(trial))] },
      ],
      note: labels.amaNote,
    },
    {
      key: "vancouver",
      label: labels.vancouver,
      entries: [
        { label: labels.referenceList, parts: [plain(nlmReference(trial))] },
      ],
      note: labels.vancouverNote,
    },
  ];
}

/* ------------------------------------------------------- ClinicalTrials.gov */

/** The parts of a ClinicalTrials.gov API v2 study record the generator reads. */
interface CtGovStudy {
  protocolSection?: {
    identificationModule?: {
      nctId?: string;
      briefTitle?: string;
      acronym?: string;
    };
    statusModule?: {
      studyFirstPostDateStruct?: { date?: string };
      lastUpdatePostDateStruct?: { date?: string };
    };
    sponsorCollaboratorsModule?: {
      responsibleParty?: {
        type?: string;
        investigatorFullName?: string;
      };
      leadSponsor?: { name?: string };
    };
  };
}

const RE_DEGREE_TOKEN = /^(?:[A-Z][a-z]{0,5}\.?){1,6}$/;
const RE_CAPITAL = /[A-Z]/g;

/** "MD", "PhD", "M.B.B.S.", "DPhil" — two or more capitals, no spaces. */
function isDegree(token: string): boolean {
  const trimmed = token.trim();
  return (
    RE_DEGREE_TOKEN.test(trimmed) &&
    (trimmed.match(RE_CAPITAL)?.length ?? 0) >= 2
  );
}

/** "Patrick Jault, MD" → "Patrick Jault"; "Jane Roe, MD, PhD" → "Jane Roe". */
export function stripDegrees(name: string): string {
  const [first, ...rest] = name.split(",");
  if (rest.length > 0 && rest.every(isDegree)) {
    return first.trim();
  }
  return name.trim();
}

/**
 * Field values from a ClinicalTrials.gov API v2 record
 * (`https://clinicaltrials.gov/api/v2/studies/{NCT}`). The author is the record's
 * responsible party: the investigator when one is named, otherwise the lead sponsor —
 * the person Citing Medicine's own example names. The acronym joins the title in
 * parentheses, as in AMA's example, unless the title already has it.
 */
export function valuesFromCtGov(
  study: CtGovStudy,
  accessed: string
): Record<string, string> | null {
  const identification = study.protocolSection?.identificationModule;
  const id = identification?.nctId ?? "";
  if (!RE_NCT.test(id)) {
    return null;
  }
  const status = study.protocolSection?.statusModule;
  const sponsors = study.protocolSection?.sponsorCollaboratorsModule;
  const investigator = stripDegrees(
    sponsors?.responsibleParty?.investigatorFullName ?? ""
  );
  const brief = (identification?.briefTitle ?? "").trim();
  const acronym = (identification?.acronym ?? "").trim();
  const title =
    acronym && !brief.includes(acronym) ? `${brief} (${acronym})` : brief;
  return {
    [F.registry]: "clinicaltrials",
    [F.identifier]: id,
    [F.title]: title,
    [F.authorType]: investigator ? "person" : "organization",
    [F.author]: investigator || (sponsors?.leadSponsor?.name ?? ""),
    [F.firstPosted]: status?.studyFirstPostDateStruct?.date ?? "",
    [F.lastUpdated]: status?.lastUpdatePostDateStruct?.date ?? "",
    [F.accessed]: accessed,
    [F.url]: `https://clinicaltrials.gov/study/${id}`,
  };
}
